import React from 'react'
import { FormApi } from 'final-form'
import { Field } from 'react-final-form'
import {
  Button,
  CheckboxField,
  InputField,
  SelectField,
} from '@mc/design-system'
import { Trans, useTranslation } from '@mc/i18n'

export type Lead = {
  firstName: string
  lastName: string
  email: string
  organizationName: string
  phoneNumber: string
  jobTitle: string
  seats: number
  useCase: string
  gdprOptIn: boolean
  whenAreYouLookingToPurchase?: string
}

type FormBodyProps = {
  form: FormApi<Lead, Partial<Lead>>
  errors: boolean
  loading: boolean
  submitted: boolean
  onSubmit: () => void
  handleValueChange: (
    value: number,
    form: FormApi<Lead, Partial<Lead>>,
    option: keyof Lead,
  ) => void
  defaultEmail: string
  clientUseCaseOptions: { value: string; label: string }[]
  showEmailCaptureCheckbox: boolean
}

export const FormBody = ({
  onSubmit,
  handleValueChange,
  form,
  defaultEmail,
  clientUseCaseOptions,
  showEmailCaptureCheckbox,
  errors,
  submitted,
  loading,
}: FormBodyProps) => {
  const { t } = useTranslation('@mc/enterprise-contact-form')

  return (
    <form
      className='d-flex flex-column mc-w-100'
      style={{ gap: 'var(--mc-scale-6)' }}
      onSubmit={onSubmit}
    >
      <div className='d-flex flex-wrap' style={{ gap: 'var(--mc-scale-6)' }}>
        <div className='flex-1' style={{ minWidth: '300px' }}>
          <Field
            component={InputField}
            id='firstName'
            name='firstName'
            label={t('groupForm.seats.form.inputFirstName.label')}
            type='text'
            placeholder={t('groupForm.seats.form.inputFirstName.label')}
            required
            autoComplete='given-name'
          />
        </div>
        <div className='flex-1' style={{ minWidth: '300px' }}>
          <Field
            component={InputField}
            id='lastName'
            name='lastName'
            label={t('groupForm.seats.form.inputLastName.label')}
            type='text'
            placeholder={t('groupForm.seats.form.inputLastName.label')}
            required
            autoComplete='family-name'
          />
        </div>
      </div>
      <div className='d-flex flex-wrap' style={{ gap: 'var(--mc-scale-6)' }}>
        <div className='flex-1' style={{ minWidth: '300px' }}>
          <Field
            component={InputField}
            id='email'
            name='email'
            label={t('groupForm.seats.form.inputEmail.label')}
            type='email'
            placeholder={t('groupForm.v2.emailPlaceholder')}
            initialValue={defaultEmail}
            required
            autoComplete='email'
          />
        </div>
        <div className='flex-1' style={{ minWidth: '300px' }}>
          <Field
            component={InputField}
            id='organizationName'
            name='organizationName'
            label={t('groupForm.seats.form.inputOrg.label')}
            type='text'
            placeholder={t('groupForm.v2.companyName')}
            required
            autoComplete='organization'
          />
        </div>
      </div>
      <div className='d-flex flex-wrap' style={{ gap: 'var(--mc-scale-6)' }}>
        <div className='flex-1' style={{ minWidth: '300px' }}>
          <Field
            component={InputField}
            id='jobTitle'
            name='jobTitle'
            label={t('groupForm.seats.form.inputTitle.label')}
            type='text'
            placeholder={t('groupForm.v2.jobTitle')}
          />
        </div>
        <div className='flex-1' style={{ minWidth: '300px' }}>
          <Field
            component={InputField}
            id='seats'
            name='seats'
            label={t('groupForm.seats.form.inputGroupSize.label')}
            type='number'
            placeholder={t('groupForm.seats.form.inputGroupSize.placeholder')}
            required
            min={5}
            max={10000}
          />
        </div>
      </div>
      <Field
        component={InputField}
        id='phoneNumber'
        name='phoneNumber'
        label={t('groupForm.seats.form.inputPhone.label')}
        type='text'
        placeholder={t('groupForm.seats.form.inputPhone.placeholder')}
        autoComplete='phone'
      />
      <Field
        component={SelectField}
        name='useCase'
        label={t('groupForm.seats.form.inputUseCase.label')}
        options={clientUseCaseOptions}
        placeholder={t('groupForm.seats.form.inputUseCase.placeholder')}
        onChange={(value: number) => handleValueChange(value, form, 'useCase')}
        required
      />
      <Field
        component={SelectField}
        name='whenAreYouLookingToPurchase'
        label={t('groupForm.seats.form.whenAreYouLookingToPurchase.label')}
        options={[
          t('groupForm.seats.form.whenAreYouLookingToPurchase.options.30'),
          t('groupForm.seats.form.whenAreYouLookingToPurchase.options.60'),
          t('groupForm.seats.form.whenAreYouLookingToPurchase.options.90'),
          t('groupForm.seats.form.whenAreYouLookingToPurchase.options.other'),
        ].map((value) => ({
          value,
          label: value,
        }))}
        placeholder={t(
          'groupForm.seats.form.whenAreYouLookingToPurchase.placeholder',
        )}
        onChange={(value: number) =>
          handleValueChange(value, form, 'whenAreYouLookingToPurchase')
        }
        required
      />

      <p className='mc-text-x-small mc-opacity--muted mc-mt-4'>
        <Trans
          i18nKey='groupForm.seats.privacyTerms'
          ns='@mc/enterprise-contact-form'
        >
          <a href='/privacy' target='_blank' className='mc-text--link'>
            {t('groupForm.v2.privacyPolicy')}
          </a>
        </Trans>
      </p>

      {showEmailCaptureCheckbox && (
        <Field
          component={CheckboxField}
          name='gdprOptIn'
          label={t('groupForm.seats.gdprLabel')}
          required
        />
      )}

      <Button
        disabled={submitted}
        type='submit'
        className='mc-m-auto mc-mt-4'
        loading={loading}
      >
        {t('groupForm.v2.contactUs')}
      </Button>

      {errors && (
        <p className='mc-text--error mc-mt-4' role='alert'>
          {t('groupForm.seats.submit.fail')}
        </p>
      )}
    </form>
  )
}
